import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Skeleton } from '@mui/material';
import clsx from 'clsx';
import { addOpacity } from 'lib/helpers';
import { isDefined } from 'lib/typeInference';

const PREFIX = 'PayTipsItemBox';

const classes = {
    box: `${PREFIX}box`,
    primary: `${PREFIX}primary`,
    secondary: `${PREFIX}secondary`,
    selected: `${PREFIX}selected`,
    skeleton: `${PREFIX}skeleton`
};

const StyledBox = styled(Box)(({ theme }) => ({
    height: '50px',
    width: '100%',
    [`& .${classes.box}`]: {
        borderRadius: theme.shape.borderRadius,
        border: `1.5px solid ${addOpacity(theme.palette.text.primary, 0.1)}`,
        flex: 1,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
    },

    [`& .${classes.primary}`]: {
        fontSize: '14px',
        lineHeight: '1.1rem',
        fontWeight: 700
    },

    [`& .${classes.secondary}`]: {
        fontSize: '13px',
        fontWeight: 500,
        lineHeight: '1.1rem',
        color: addOpacity(theme.palette.text.primary, 0.4)
    },

    [`& .${classes.selected}`]: {
        borderColor: theme.palette.text.primary
    },

    [`& .${classes.skeleton}`]: {
        borderRadius: theme.shape.borderRadius,
        flex: 1
    }
}));

interface PayTipsItemBoxProps {
    primary: string;
    secondary?: string;
    selected?: boolean;
    onClick: () => void;
    loading?: boolean;
    gutterRight?: boolean;
    primaryClassName?: string;
}

export const PayTipsItemBox: React.FC<React.PropsWithChildren<PayTipsItemBoxProps>> = ({
    primary,
    secondary,
    selected,
    loading,
    gutterRight = true,
    primaryClassName,
    onClick
}) => {
    if (loading) {
        return (
            <StyledBox mr={gutterRight ? 1 : 0}>
                <Skeleton className={classes.skeleton} variant="rectangular" height="50px" width="100%" />
            </StyledBox>
        );
    }
    return (
        <StyledBox mr={gutterRight ? 1 : 0} onClick={onClick}>
            <Box
                className={clsx(classes.box, {
                    [classes.selected]: !!selected
                })}
            >
                {isDefined(secondary) && <Typography className={classes.secondary}>{secondary}</Typography>}
                <Typography className={clsx(classes.primary, primaryClassName)}>{primary}</Typography>
            </Box>
        </StyledBox>
    );
};
